// BM-H398 - s7_residue. One of three residue blocks is live, selected by
// s5_structure.chosen_structure; willsPayload.normalisePayload retires the
// others. Every field here is one the engine reads for a clause, a hard stop
// or a warning. The hard stops the engine raises (fractions not summing to
// one, missing ultimate default, onward IPDI, cohabitation wording, and so
// on) are shown to the client from the engine's own reason strings; nothing
// is re-implemented client side.

import { NumberInput, MoneyInput, QuestionRow, SectionIntro, Select, TextInput, YesNo, sectionProps } from "./fields";
import PersonPicker, { PeopleList } from "./PersonPicker";
import { STRUCTURES } from "./willsPayload";
import { CallbackHint, UnsupportedNotice } from "./removedOptions";

const ROUTE_OPTIONS = [
  { value: "absolute_to_spouse_then_children_per_stirpes", label: "To my spouse or partner, then to my children if they die before me" },
  { value: "absolute_to_named_children_equally", label: "To my children equally" },
  { value: "absolute_to_named_beneficiaries", label: "To the people I name, in the shares I set" },
];

// BM-H3636: named substitutes, the two year IPDI window, discretionary or onward remainders, cohabitation as an
// end event, the IPDI-to-spouse and named-people surplus routes and the home carve-out are no longer offered
// (solicitor ruling, 5 Oct 2026). Saved choices are kept and flagged by UnsupportedNotice.
export const SUBSTITUTION_OPTIONS = [
  { value: "per_stirpes", label: "Their share passes to their children" },
  { value: "surviving_class", label: "Their share passes to the surviving beneficiaries" },
];

export const HOME_STRATEGY_OPTIONS = [
  { value: "s144_appointment_out_intention", label: "Let my trustees appoint it out within two years" },
  { value: "discuss", label: "I would like to discuss this" },
];

const LONGSTOP_KIND = [
  { value: "named_person", label: "A person" },
  { value: "charity", label: "A charity" },
];

const RESPONSIBILITY = [
  { value: "life_tenant", label: "The person living there" },
  { value: "trustees", label: "The trustees, from the estate" },
];

export function Longstop({ s, set, payload, update, person }) {
  const ls = s.longstop_beneficiary || {};
  const edit = (patch) => set({ longstop_beneficiary: { ...ls, ...patch } });
  return (
    <div data-testid="s7-longstop">
      <QuestionRow qid="s7_residue.longstop_beneficiary.kind" label="If everyone you have named has died before you, who should take the residue?">
        <Select testid="s7-longstop-kind" value={ls.kind} options={LONGSTOP_KIND} onChange={(v) => set({ longstop_beneficiary: v ? { kind: v, person_id: null, charity_name: null, charity_number_uk: null, is_registered_uk_charity: v === "charity" ? true : undefined } : null })} />
      </QuestionRow>
      {ls.kind === "named_person" && (
        <PersonPicker payload={payload} update={update} qid="s7_residue.longstop_beneficiary.person_id" label="Who?" value={ls.person_id} exclude={[person?.id]} onChange={(pid) => edit({ person_id: pid })} testid="s7-longstop-person" relationshipRequired />
      )}
      {ls.kind === "charity" && (
        <>
          <QuestionRow qid="s7_residue.longstop_beneficiary.charity_name" label="Charity's full registered name">
            <TextInput testid="s7-longstop-charity-name" value={ls.charity_name} onChange={(v) => edit({ charity_name: v || null })} />
          </QuestionRow>
          <QuestionRow qid="s7_residue.longstop_beneficiary.charity_number_uk" label="Registered charity number" evidence>
            <TextInput testid="s7-longstop-charity-number" value={ls.charity_number_uk} onChange={(v) => edit({ charity_number_uk: v || null })} />
          </QuestionRow>
        </>
      )}
    </div>
  );
}

// BM-H3960 item 33: the fraction boxes are hidden under "Equal shares" but their numbers used to stay on the records, and the
// backend then refused the Will for carrying fraction data under equal shares. Switching to equal shares now clears them.
export function withoutFractions(beneficiaries) {
  return (beneficiaries || []).map((b) => {
    const { fraction_numerator, fraction_denominator, ...rest } = b || {};
    return rest;
  });
}

function OutrightBlock({ s, set, payload, update, person, answers }) {
  const route = s.primary_route || { kind: null };
  const beneficiaries = s.beneficiaries || [];
  const children = (answers.s2_family.children || []).map((c) => c.person_id).filter(Boolean);

  function setKind(kind) {
    const spouse = answers.s1_testator.spouse_person_id || null;
    if (kind === "absolute_to_spouse_then_children_per_stirpes") {
      set({ primary_route: { kind, spouse_person_id: spouse, children_person_ids: children }, shape: "single", beneficiaries: spouse ? [{ person_id: spouse }] : [], substitution_rule: "per_stirpes" });
    } else if (kind === "absolute_to_named_children_equally") {
      // BM-H3926: no substitution rule for this route. The sealed wording (C-14a-named) gives a failed child's share to the
      // other named children and never reads a rule, so saving "per_stirpes" here recorded a choice the Will does not follow.
      set({ primary_route: { kind, children_person_ids: children }, shape: "multiple_equal", beneficiaries: children.map((pid) => ({ person_id: pid })), substitution_rule: undefined });
    } else if (kind === "absolute_to_named_beneficiaries") {
      set({ primary_route: { kind, named_beneficiary_person_ids: [] }, shape: "multiple_equal", beneficiaries: [], substitution_rule: route.kind === kind ? s.substitution_rule || null : null });
    } else {
      set({ primary_route: { kind: null }, shape: undefined, beneficiaries: undefined, substitution_rule: undefined });
    }
  }

  function setBeneficiaries(items) {
    set({ beneficiaries: items, primary_route: { ...route, named_beneficiary_person_ids: items.map((b) => b.person_id).filter(Boolean) } });
  }

  return (
    <div data-testid="s7-outright">
      <QuestionRow qid="s7_residue.primary_route.kind" label="Who should receive the rest of your estate?">
        <Select testid="s7-route-kind" value={route.kind} options={ROUTE_OPTIONS} onChange={setKind} />
      </QuestionRow>
      {route.kind === "absolute_to_spouse_then_children_per_stirpes" && (
        <PersonPicker payload={payload} update={update} qid="s7_residue.beneficiaries.person_id" label="Your spouse or partner" value={route.spouse_person_id} exclude={[person?.id]} onChange={(pid) => set({ primary_route: { ...route, spouse_person_id: pid }, beneficiaries: pid ? [{ person_id: pid }] : [] })} testid="s7-spouse" defaultRelationship="spouse" />
      )}
      {route.kind === "absolute_to_named_beneficiaries" && (
        <>
          <QuestionRow qid="s7_residue.beneficiaries" label="Equal shares, or shares you set?">
            <Select testid="s7-shape" value={s.shape} options={[{ value: "multiple_equal", label: "Equal shares" }, { value: "multiple_fractional", label: "Shares I set" }]} onChange={(v) => set(v === "multiple_equal" ? { shape: v, beneficiaries: withoutFractions(beneficiaries) } : { shape: v })} />
          </QuestionRow>
          <PeopleList
            payload={payload}
            update={update}
            items={beneficiaries}
            onChange={setBeneficiaries}
            qid="s7_residue.beneficiaries.person_id"
            label="Beneficiary"
            testid="s7-beneficiaries"
            exclude={[person?.id]}
            renderExtra={(b, i) => s.shape === "multiple_fractional" && (
              <QuestionRow qid="s7_residue.beneficiaries.fraction" label="Share as a fraction" testid={`s7-beneficiary-${i}-q-fraction`}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <NumberInput testid={`s7-beneficiary-${i}-numerator`} min="1" value={b.fraction_numerator} onChange={(v) => setBeneficiaries(beneficiaries.map((x, j) => (j === i ? { ...x, fraction_numerator: v } : x)))} />
                  <span>over</span>
                  <NumberInput testid={`s7-beneficiary-${i}-denominator`} min="1" value={b.fraction_denominator} onChange={(v) => setBeneficiaries(beneficiaries.map((x, j) => (j === i ? { ...x, fraction_denominator: v } : x)))} />
                </div>
              </QuestionRow>
            )}
          />
        </>
      )}
      {route.kind === "absolute_to_named_beneficiaries" && (
        <QuestionRow qid="s7_residue.substitution_rule" label="If one of them dies before you">
          <Select testid="s7-substitution-rule" value={s.substitution_rule} options={SUBSTITUTION_OPTIONS} onChange={(v) => set({ substitution_rule: v })} />
        </QuestionRow>
      )}
      {route.kind === "absolute_to_named_beneficiaries" && (
        <CallbackHint testid="s7-substitute-hint">If one of them dies before you, their share can pass to their children or to the others.</CallbackHint>
      )}
    </div>
  );
}

// BM-H3933 (solicitor, 7 Oct 2026): a life tenant who is also named to take the capital is not forbidden, because it may be
// intended, but the Will is not produced until a fee earner has approved it. The remainder picker hides the life tenant while
// people are being picked, yet a client who names the remaindermen first and then changes the life tenant keeps the saved
// remainderman (and the picker, which hides that person, no longer shows them). So say it plainly and leave the choice alone.
export function LifeTenantRemaindermanNotice({ lifeTenantId, remaindermen, payload }) {
  if (!lifeTenantId || !(remaindermen || []).some((r) => r && r.person_id === lifeTenantId)) return null;
  const name = ((payload.couple.people_register || []).find((p) => p.id === lifeTenantId) || {}).full_legal_name;
  return (
    <div role="note" data-testid="s7-life-tenant-also-remainderman" style={{ border: "1px solid #b45309", background: "#fffbeb", borderRadius: 6, padding: "10px 12px", margin: "10px 0 16px", fontSize: 14 }}>
      <strong>{name || "The person you chose to have the benefit during their lifetime"} is also named to take the capital when the life interest ends.</strong>{" "}
      That can be what you want, but it can change who benefits, how long the trust lasts and the tax position. Your Will cannot be produced
      until your fee earner has gone through it with you and approved it. If you did not mean this, remove them from the list above.
    </div>
  );
}

function IpdiBlock({ s, set, payload, update, person }) {
  const lt = s.life_tenant || {};
  const terms = s.life_interest_terms || {};
  const cap = terms.power_to_apply_capital_to_life_tenant?.cap || {};
  const roo = terms.right_of_occupation || {};
  const cond = roo.conditions || {};
  const te = s.termination_events || {};
  const setTerms = (patch) => set({ life_interest_terms: { ...terms, ...patch } });
  const setRoo = (patch) => setTerms({ right_of_occupation: { ...roo, ...patch } });

  return (
    <div data-testid="s7-ipdi">
      <PersonPicker payload={payload} update={update} qid="s7_residue.life_tenant.person_id" label="Who should have the benefit during their lifetime?" value={lt.person_id} exclude={[person?.id]} onChange={(pid) => set({ life_tenant: { type: "person", person_id: pid } })} testid="s7-life-tenant" defaultRelationship="spouse" />
      <CallbackHint testid="s7-commencement-hint">The trust starts immediately on your death.</CallbackHint>
      <QuestionRow qid="s7_residue.life_interest_terms.power_to_apply_capital_to_life_tenant.cap" label="Can they also receive capital, and up to what limit?">
        <Select testid="s7-capital-cap" value={cap.type} options={[{ value: "none", label: "Income only" }, { value: "up_to_percentage", label: "Capital up to a percentage" }, { value: "unlimited", label: "Capital without limit, at the trustees' discretion" }]} onChange={(v) => setTerms({ power_to_apply_capital_to_life_tenant: { granted: v !== "none", cap: { type: v, percentage: v === "up_to_percentage" ? cap.percentage ?? null : undefined } } })} />
        {cap.type === "up_to_percentage" && (
          <div style={{ marginTop: 6 }}>
            <NumberInput testid="s7-capital-percentage" min="1" value={cap.percentage} onChange={(v) => setTerms({ power_to_apply_capital_to_life_tenant: { granted: true, cap: { type: "up_to_percentage", percentage: v } } })} />
          </div>
        )}
      </QuestionRow>
      <QuestionRow qid="s7_residue.life_interest_terms.right_of_occupation.granted" label="Should they have the right to live in a property?">
        <YesNo testid="s7-roo-granted" value={roo.granted ?? null} onChange={(v) => setRoo({ granted: v })} />
      </QuestionRow>
      {roo.granted === true && (
        <>
          <QuestionRow qid="s7_residue.life_interest_terms.right_of_occupation.scope" label="Which property?">
            <Select testid="s7-roo-scope" value={roo.scope} options={[{ value: "any_trust_property", label: "Any property held in the trust" }, { value: "specific_property", label: "One specific property" }]} onChange={(v) => setRoo({ scope: v })} />
          </QuestionRow>
          {roo.scope === "specific_property" && (
            <QuestionRow qid="s7_residue.life_interest_terms.right_of_occupation.specific_property_description" label="Describe the property">
              <TextInput testid="s7-roo-description" value={roo.specific_property_description} onChange={(v) => setRoo({ specific_property_description: v || null })} />
            </QuestionRow>
          )}
          <QuestionRow qid="s7_residue.life_interest_terms.right_of_occupation.conditions" label="Who pays for each of these while they live there?">
            {[["outgoings_responsibility", "Outgoings"], ["repairs_responsibility", "Repairs"], ["insurance_responsibility", "Insurance"], ["council_tax_responsibility", "Council tax"]].map(([f, label]) => (
              <div key={f} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <span style={{ minWidth: 100, fontSize: 14 }}>{label}</span>
                <Select testid={`s7-roo-${f}`} value={cond[f]} options={RESPONSIBILITY} onChange={(v) => setRoo({ conditions: { ...cond, [f]: v } })} />
              </div>
            ))}
          </QuestionRow>
        </>
      )}
      <QuestionRow qid="s7_residue.termination_events.on_remarriage" label="Should the life interest end if they remarry or enter a new civil partnership?">
        <YesNo testid="s7-on-remarriage" value={te.on_remarriage ?? null} onChange={(v) => set({ termination_events: { ...te, on_remarriage: v } })} />
      </QuestionRow>
      <CallbackHint testid="s7-cohabitation-hint">The life interest ends if they remarry or form a new civil partnership, if you choose that above. It does not end because they live with someone.</CallbackHint>
      <CallbackHint testid="s7-remainder-hint">When the life interest ends, the capital passes to the people you name below, outright and in equal shares.</CallbackHint>
      <PeopleList payload={payload} update={update} items={s.remaindermen || []} onChange={(items) => set({ remaindermen: items })} qid="s7_residue.remaindermen" label="Person who takes the capital" testid="s7-remaindermen" exclude={[person?.id, lt.person_id].filter(Boolean)} />
      <LifeTenantRemaindermanNotice lifeTenantId={lt.person_id} remaindermen={s.remaindermen} payload={payload} />
    </div>
  );
}

// BM-H3931 (solicitor's answer 3, 7 Oct 2026): a spouse or civil partner must not be a discretionary beneficiary of this
// trust. The picker does not offer them. A saved answer that already has one in the class is KEPT (never silently dropped)
// and flagged here; the server also blocks the Will on it (backend/hub/wills_assembly/nrbdt_exclusions.py).
export const SPOUSE_RELATIONSHIPS = ["spouse", "civil_partner"];

export function spouseIds(payload, answers) {
  const recorded = answers?.s1_testator?.spouse_person_id;
  const ids = (payload?.couple?.people_register || []).filter((p) => SPOUSE_RELATIONSHIPS.includes(p.relationship)).map((p) => p.id);
  return recorded ? [...new Set([recorded, ...ids])] : ids;
}

export function spousesInClass(payload, answers, members) {
  const spouses = spouseIds(payload, answers);
  return (members || []).filter((m) => m.type === "person" && spouses.includes(m.person_id))
    .map((m) => (payload?.couple?.people_register || []).find((p) => p.id === m.person_id) || { id: m.person_id, full_legal_name: "A person in the list" });
}

function NrbdtBlock({ s, set, payload, update, person, answers }) {
  const scope = s.nrbdt_scope || {};
  const def = scope.nrb_sum_definition || {};
  const dbc = s.discretionary_beneficiary_class || { members: [] };
  const tp = s.trustee_powers || {};
  const ap = s.accumulation_power || {};
  const setScope = (patch) => set({ nrbdt_scope: { ...scope, ...patch } });
  const personMembers = (dbc.members || []).filter((m) => m.type === "person");
  const issueClass = (dbc.members || []).some((m) => m.type === "class" && m.class === "issue");
  const spouses = spouseIds(payload, answers);
  const flagged = spousesInClass(payload, answers, dbc.members);
  const setMembers = (people, includeIssue) => set({ discretionary_beneficiary_class: { members: [...people.map((p) => ({ type: "person", person_id: p.person_id })), ...(includeIssue ? [{ type: "class", class: "issue" }] : [])] } });

  return (
    <div data-testid="s7-nrbdt">
      <QuestionRow qid="s7_residue.nrbdt_scope.shape" label="What should the discretionary trust hold?">
        <Select testid="s7-nrbdt-shape" value={scope.shape} options={[{ value: "nrb_sum_only", label: "A sum equal to my nil rate band" }, { value: "whole_residue", label: "The whole of the residue" }]} onChange={(v) => setScope({ shape: v })} />
      </QuestionRow>
      {scope.shape === "nrb_sum_only" && (
        <>
          <QuestionRow qid="s7_residue.nrbdt_scope.nrb_sum_definition.type" label="How should the nil rate band sum be defined?">
            <Select testid="s7-nrb-definition" value={def.type} options={[{ value: "available_inc_transferable", label: "The nil rate band available at my death, including any transferred" }, { value: "fixed_sum", label: "A fixed sum" }]} onChange={(v) => setScope({ nrb_sum_definition: { type: v, amount_gbp: v === "fixed_sum" ? def.amount_gbp ?? null : undefined } })} />
          </QuestionRow>
          {def.type === "fixed_sum" && (
            <QuestionRow qid="s7_residue.nrbdt_scope.nrb_sum_definition.amount_gbp" label="How much?">
              <MoneyInput testid="s7-nrb-fixed-sum" value={def.amount_gbp} onChange={(v) => setScope({ nrb_sum_definition: { type: "fixed_sum", amount_gbp: v } })} />
            </QuestionRow>
          )}
          <CallbackHint testid="s7-surplus-hint">The rest of the residue passes outright to your spouse or civil partner.</CallbackHint>
        </>
      )}
      {flagged.length > 0 && (
        <div role="alert" data-testid="s7-dbc-spouse-notice" style={{ border: "1px solid #b45309", background: "#fffbeb", borderRadius: 6, padding: "10px 12px", margin: "0 0 12px", fontSize: 14 }}>
          <strong>A spouse or civil partner cannot be one of the discretionary beneficiaries.</strong>
          <ul style={{ margin: "6px 0 6px 18px" }}>
            {flagged.map((p) => <li key={p.id} data-testid={`s7-dbc-spouse-${p.id}`}>{p.full_legal_name || "(unnamed)"} is your spouse or civil partner and is in this list.</li>)}
          </ul>
          Your answer is saved and has not been changed. Please take them out of the list. Your Will cannot be produced while they are in it.
        </div>
      )}
      <PeopleList payload={payload} update={update} items={personMembers} onChange={(items) => setMembers(items, issueClass)} qid="s7_residue.discretionary_beneficiary_class.members" label="Discretionary beneficiary" testid="s7-dbc" exclude={[person?.id]} excludeUnlessSelected={spouses} />
      <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 14, margin: "8px 0 18px" }}>
        <input type="checkbox" data-testid="s7-dbc-issue" checked={issueClass} onChange={(e) => setMembers(personMembers, e.target.checked)} />
        Include my children and their descendants as a class
      </label>
      <QuestionRow qid="s7_residue.trustee_powers.power_to_add_beneficiaries" label="May the trustees add beneficiaries later?">
        <YesNo testid="s7-power-add" value={tp.power_to_add_beneficiaries ?? null} onChange={(v) => set({ trustee_powers: { ...tp, power_to_add_beneficiaries: v } })} />
      </QuestionRow>
      <QuestionRow qid="s7_residue.trustee_powers.power_to_remove_beneficiaries" label="May the trustees remove beneficiaries later?">
        <YesNo testid="s7-power-remove" value={tp.power_to_remove_beneficiaries ?? null} onChange={(v) => set({ trustee_powers: { ...tp, power_to_remove_beneficiaries: v } })} />
      </QuestionRow>
      <QuestionRow qid="s7_residue.accumulation_power.granted" label="May the trustees accumulate income within the trust?">
        <YesNo testid="s7-accumulate" value={ap.granted ?? null} onChange={(v) => set({ accumulation_power: { granted: v, accumulation_period_years: v ? ap.accumulation_period_years ?? null : undefined } })} />
      </QuestionRow>
      {ap.granted === true && (
        <QuestionRow qid="s7_residue.accumulation_power.accumulation_period_years" label="For how many years?">
          <NumberInput testid="s7-accumulate-years" min="1" value={ap.accumulation_period_years} onChange={(v) => set({ accumulation_power: { granted: true, accumulation_period_years: v } })} />
        </QuestionRow>
      )}
      <QuestionRow qid="s7_residue.rnrb_home_planning.strategy" label="If your home is part of the residue, how should it be treated?">
        <Select testid="s7-rnrb-strategy" value={s.rnrb_home_planning?.strategy || (s.rnrb_home_planning ? "discuss" : null)} options={HOME_STRATEGY_OPTIONS} onChange={(v) => set({ rnrb_home_planning: v === "discuss" ? { strategy: null } : v ? { strategy: v } : undefined })} />
      </QuestionRow>
    </div>
  );
}

export default function S7Residue({ section, setSection, payload, update, person, answers }) {
  const { s, set, nk, setNk } = sectionProps(section, setSection);
  const a = answers || payload.submission.answers;
  const structure = a.s5_structure.chosen_structure;
  const common = { s, set, payload, update, person, answers: a };

  return (
    <div data-testid="section-s7_residue">
      <UnsupportedNotice answers={a} section="s7_residue" />
      <SectionIntro title="The rest of your estate">
        {structure === STRUCTURES.DISCUSS || !structure
          ? "You chose to discuss how your estate should pass, or have not chosen yet. Your fee earner will walk you through it. You can still tell us about a survivorship period and an ultimate default."
          : "The residue is everything left after specific gifts, debts, taxes and expenses. For most people it is the biggest single part of the estate."}
      </SectionIntro>

      <QuestionRow qid="s7_residue.survivorship_days" label="Survivorship period (days)" notKnown={nk("survivorship_days")} onNotKnown={(on) => setNk("survivorship_days", on)}>
        <NumberInput testid="s7-survivorship-days" value={s.survivorship_days} disabled={nk("survivorship_days")} onChange={(v) => set({ survivorship_days: v })} />
      </QuestionRow>

      {structure === STRUCTURES.A && <OutrightBlock {...common} />}
      {structure === STRUCTURES.B && <IpdiBlock {...common} />}
      {structure === STRUCTURES.C && <NrbdtBlock {...common} />}

      <div style={{ marginTop: 18 }}>
        <QuestionRow qid="s7_residue.ultimate_default" label="The ultimate default" hint="Every residuary gift has a longstop. This is who takes if everything else fails.">
          <Longstop {...common} />
        </QuestionRow>
      </div>
    </div>
  );
}
